import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  ModalHeader,
  ModalSubtitle,
  ModalBand,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { InlineAlert } from '@/components/notifications';
import { cn } from '@/lib/utils';
import { formatNumber as fmtNumber, formatPercent } from '@/lib/number-format';
import type { KPI, KPITypes } from '@shared/schema';
import type { KPIUpsertPayload } from '@/services/api';
import {
  Loader2,
  Rocket,
  ArchiveRestore,
  Trash2,
  Ban,
  Plus,
  Activity,
} from 'lucide-react';

type KPIManagerMode = 'create' | 'edit';

interface KPIManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kpis: KPI[];
  loading?: boolean;
  onCreate: (input: KPIUpsertPayload) => Promise<void>;
  onUpdate: (id: string, input: KPIUpsertPayload) => Promise<void>;
  onPublish: (id: string) => Promise<void>;
  onRetire: (id: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  selectedKpiIds: string[];
  onSelectionChange: (ids: string[]) => Promise<void>;
  selectionLimit?: number;
  selectionSaving?: boolean;
}

interface KPIFormState {
  id: string;
  label: string;
  value: string;
  change: string;
  comparison: string;
  icon: string;
  type: KPITypes;
  description: string;
  thresholdsJson: string;
  sortOrder: string;
}

const emptyForm: KPIFormState = {
  id: '',
  label: '',
  value: '',
  change: '',
  comparison: '',
  icon: 'gauge',
  type: 'number',
  description: '',
  thresholdsJson: '',
  sortOrder: '',
};

const statusVariant: Record<
  string,
  'default' | 'secondary' | 'outline' | 'destructive' | 'success' | 'warning'
> = {
  draft: 'secondary',
  published: 'success',
  retired: 'outline',
};

const formatStatus = (status?: string) => {
  if (!status) return 'Unknown';
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const inputBaseClass =
  'border border-border/60 bg-bg-0 text-text-900 placeholder:text-text-500 transition-shadow focus-visible:border-brand-red focus-visible:ring-2 focus-visible:ring-brand-red/30 focus-visible:ring-offset-0';

export function KPIManagerDialog({
  open,
  onOpenChange,
  kpis,
  loading = false,
  onCreate,
  onUpdate,
  onPublish,
  onRetire,
  onDelete,
  selectedKpiIds,
  onSelectionChange,
  selectionLimit = 4,
  selectionSaving = false,
}: KPIManagerDialogProps) {
  const [mode, setMode] = useState<KPIManagerMode>('create');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState<KPIFormState>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingAction, setPendingAction] = useState<
    { id: string; action: 'publish' | 'retire' | 'delete' } | null
  >(null);
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const listScrollRef = useRef<HTMLDivElement>(null);
  const [headerElevated, setHeaderElevated] = useState(false);
  const [footerElevated, setFooterElevated] = useState(false);
  const [selectedKpis, setSelectedKpis] = useState<string[]>(selectedKpiIds);
  const [selectionError, setSelectionError] = useState<string | null>(null);

  const selectedKPI = useMemo(
    () => kpis.find((item) => item.id === selectedId) ?? null,
    [kpis, selectedId],
  );

  useEffect(() => {
    setSelectedKpis(selectedKpiIds);
  }, [selectedKpiIds]);

  useEffect(() => {
    if (!open) {
      setSelectionError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setMode('create');
      setSelectedId(null);
      setForm(emptyForm);
      setFormError(null);
      setIsSaving(false);
      setPendingAction(null);
      return;
    }

    if (selectedKPI) {
      setMode('edit');
      setForm({
        id: selectedKPI.id,
        label: selectedKPI.label,
        value:
          selectedKPI.value !== undefined ? String(selectedKPI.value) : '',
        change:
          selectedKPI.change !== undefined && selectedKPI.change !== null
            ? String(selectedKPI.change)
            : '',
        comparison: selectedKPI.comparison ?? '',
        icon: selectedKPI.icon ?? 'gauge',
        type: (selectedKPI.type ?? 'number') as KPITypes,
        description: selectedKPI.description ?? '',
        thresholdsJson: selectedKPI.thresholds
          ? JSON.stringify(selectedKPI.thresholds, null, 2)
          : '',
        sortOrder:
          selectedKPI.sortOrder !== undefined &&
          selectedKPI.sortOrder !== null
            ? String(selectedKPI.sortOrder)
            : '',
      });
    } else {
      setMode('create');
      setForm(emptyForm);
    }
  }, [open, selectedKPI]);

  useEffect(() => {
    if (!open) {
      setHeaderElevated(false);
      setFooterElevated(false);
      return;
    }

    const updateScrollState = () => {
      const contentNode = contentScrollRef.current;
      const listNode = listScrollRef.current;

      const contentScroll = contentNode?.scrollTop ?? 0;
      const listScroll = listNode?.scrollTop ?? 0;

      setHeaderElevated(contentScroll > 4 || listScroll > 4);

      if (contentNode) {
        const remaining =
          contentNode.scrollHeight - contentNode.clientHeight - contentScroll;
        setFooterElevated(remaining > 8);
      } else {
        setFooterElevated(false);
      }
    };

    updateScrollState();

    const contentNode = contentScrollRef.current;
    const listNode = listScrollRef.current;
    contentNode?.addEventListener('scroll', updateScrollState);
    listNode?.addEventListener('scroll', updateScrollState);

    return () => {
      contentNode?.removeEventListener('scroll', updateScrollState);
      listNode?.removeEventListener('scroll', updateScrollState);
    };
  }, [open]);

  const selectionCount = selectedKpis.length;
  const toggleSelection = async (kpiId: string) => {
    if (selectionSaving) {
      return;
    }
    setSelectionError(null);
    const current = selectedKpis;
    const isSelected = current.includes(kpiId);
    let next: string[];
    if (isSelected) {
      next = current.filter((id) => id !== kpiId);
    } else {
      next = [...current, kpiId];
      if (next.length > selectionLimit) {
        setSelectionError(`You can select up to ${selectionLimit} KPIs.`);
        return;
      }
    }
    setSelectedKpis(next);
    try {
      await onSelectionChange(next);
    } catch (error: any) {
      const message = error instanceof Error ? error.message : 'Unable to update KPI selection.';
      setSelectionError(message);
      setSelectedKpis(current);
    }
  };

  const resetToCreate = () => {
    setMode('create');
    setSelectedId(null);
    setForm(emptyForm);
    setFormError(null);
  };

  const handleInputChange =
    (field: keyof KPIFormState) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleTypeChange = (value: KPITypes) => {
    setForm((prev) => ({ ...prev, type: value }));
  };

  const parseThresholds = (
    input: string,
  ): Record<string, number> | undefined | null => {
    if (!input || input.trim().length === 0) return undefined;
    try {
      const parsed = JSON.parse(input);
      if (parsed === null) {
        return null;
      }
      if (typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('Thresholds must be a JSON object');
      }
      const normalized: Record<string, number> = {};
      for (const [key, value] of Object.entries(parsed)) {
        const numericValue = Number(value);
        if (Number.isNaN(numericValue)) {
          throw new Error(`Threshold "${key}" must be numeric`);
        }
        normalized[key] = numericValue;
      }
      return normalized;
    } catch (error: any) {
      setFormError(error.message || 'Invalid thresholds JSON');
      throw error;
    }
  };

  const buildPayload = (): KPIUpsertPayload => {
    if (!form.id.trim()) {
      throw new Error('KPI identifier is required');
    }
    if (!form.label.trim()) {
      throw new Error('Label is required');
    }
    if (!form.comparison.trim()) {
      throw new Error('Comparison label is required');
    }
    const value = Number(form.value);
    if (Number.isNaN(value)) {
      throw new Error('Value must be numeric');
    }

    const changeValue =
      form.change.trim().length > 0 ? Number(form.change) : undefined;
    if (form.change.trim().length > 0 && Number.isNaN(changeValue)) {
      throw new Error('Change must be numeric');
    }

    const sortOrderValue =
      form.sortOrder.trim().length > 0 ? Number(form.sortOrder) : undefined;
    if (form.sortOrder.trim().length > 0 && Number.isNaN(sortOrderValue)) {
      throw new Error('Sort order must be numeric');
    }

    const thresholds = parseThresholds(form.thresholdsJson);

    return {
      id: form.id.trim(),
      label: form.label.trim(),
      value,
      change: changeValue ?? null,
      comparison: form.comparison.trim(),
      icon: form.icon.trim() || 'gauge',
      type: form.type,
      thresholds: thresholds ?? undefined,
      description: form.description.trim() || undefined,
      sort_order: sortOrderValue,
      recorded_at: new Date().toISOString(),
    };
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    try {
      const payload = buildPayload();
      setIsSaving(true);
      if (mode === 'create') {
        await onCreate(payload);
        resetToCreate();
      } else if (selectedId) {
        await onUpdate(selectedId, payload);
      }
    } catch (error: any) {
      if (error instanceof Error && error.message) {
        setFormError(error.message);
      }
      console.error('Failed to save KPI', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async (id: string) => {
    setPendingAction({ id, action: 'publish' });
    try {
      await onPublish(id);
    } finally {
      setPendingAction(null);
    }
  };

  const handleRetire = async (id: string) => {
    setPendingAction({ id, action: 'retire' });
    try {
      await onRetire(id);
    } finally {
      setPendingAction(null);
    }
  };

  const handleDelete = async (id: string) => {
    setPendingAction({ id, action: 'delete' });
    try {
      await onDelete(id);
      if (selectedId === id) {
        resetToCreate();
      }
    } finally {
      setPendingAction(null);
    }
  };

  const renderList = () => {
    if (loading) {
      return (
        <div className="flex h-full items-center justify-center text-sm text-brand-red-600">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading KPIs…
        </div>
      );
    }

    if (!kpis.length) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 px-7 text-center text-sm text-text-500">
          <Activity className="h-8 w-8 text-brand-red/60" />
          <div>
            <p className="text-base font-medium text-text-900">No KPIs created yet</p>
            <p className="text-xs text-text-500">
              Use the form to define the first metric for this tenant.
            </p>
          </div>
          <Button
            size="sm"
            onClick={resetToCreate}
            data-testid="kpi-manager-new-button"
            variant="default"
            className="bg-brand-red text-white hover:bg-brand-red/90"
          >
            <Plus className="mr-1.5 h-4 w-4" />
            New KPI
          </Button>
        </div>
      );
    }

    const formatNumber = { format: (v: number) => fmtNumber(v) };
    const selectedLabels = selectedKpis
      .map((id) => kpis.find((kpi) => kpi.id === id)?.label)
      .filter((label): label is string => Boolean(label));

    return (
      <div className="space-y-4 pr-1.5">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-text-500">
              Dashboard KPIs ({selectionCount}/{selectionLimit})
            </p>
            {selectionSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin text-text-400" /> : null}
          </div>
          {selectedLabels.length ? (
            <div className="flex flex-wrap gap-2">
              {selectedLabels.map((label) => (
                <Badge
                  key={label}
                  variant="secondary"
                  className="border border-rose-100 bg-rose-50 text-rose-700"
                >
                  {label}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-text-400">
              Select up to {selectionLimit} KPIs to highlight on the Overview dashboard.
            </p>
          )}
          {selectionError ? (
            <InlineAlert tone="warning" layout="compact" description={selectionError} />
          ) : null}
        </div>

        {kpis.map((kpi) => {
          const kpiKey = kpi.kpiUuid ?? kpi.id;
          const status = kpi.status ?? 'draft';
          const isPending = pendingAction?.id === kpi.id;
          const isEditing = selectedId === kpi.id;
          const isSelectedForDashboard = selectedKpis.includes(kpi.id);
          const reachLimit = !isSelectedForDashboard && selectionCount >= selectionLimit;
          const disableCheckbox = selectionSaving || (reachLimit && !isSelectedForDashboard);

          const changeDisplay =
            typeof kpi.change === 'number'
              ? formatPercent(kpi.change, { decimals: 2, sign: 'always' })
              : '—';
          const changeTone =
            typeof kpi.change === 'number'
              ? kpi.change > 0
                ? 'text-emerald-600'
                : kpi.change < 0
                  ? 'text-brand-red'
                  : 'text-text-700'
              : 'text-text-700';

          const badgeTone =
            status === 'published'
              ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
              : status === 'draft'
                ? 'bg-slate-100 text-slate-600 border border-slate-200'
                : 'bg-amber-50 text-amber-700 border border-amber-100';

          return (
            <div
              key={kpiKey}
              role="button"
              tabIndex={0}
              onClick={() => {
                setSelectedId(kpi.id);
                setMode('edit');
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setSelectedId(kpi.id);
                  setMode('edit');
                }
              }}
              className={cn(
                'group relative w-full rounded-[10px] border border-border/40 bg-bg-1 px-4 py-3 text-left shadow-sm transition-all',
                'hover:-translate-y-[2px] hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red/35 focus-visible:ring-offset-2',
                'before:absolute before:inset-y-3 before:left-0 before:w-[2px] before:rounded-r-[10px] before:bg-border/40 before:transition-colors before:duration-200',
                isEditing && 'border-brand-red/40 bg-rose-surface shadow-md before:bg-brand-red'
              )}
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={isSelectedForDashboard}
                  onCheckedChange={() => void toggleSelection(kpi.id)}
                  disabled={disableCheckbox}
                  className="mt-1"
                  aria-label={isSelectedForDashboard ? 'Remove KPI from dashboard' : 'Add KPI to dashboard'}
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                />
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-text-900">{kpi.label}</span>
                        <Badge
                          variant={statusVariant[status] ?? 'secondary'}
                          className={cn(
                            'px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide',
                            badgeTone
                          )}
                        >
                          {formatStatus(status)}
                        </Badge>
                      </div>
                      {kpi.description && (
                        <p className="text-xs text-text-500 line-clamp-2">{kpi.description}</p>
                      )}
                      <div className="grid grid-cols-3 gap-3 pt-1.5 text-xs text-text-500">
                        <div>
                          <span className="block text-[11px] font-medium uppercase tracking-wide text-text-400">
                            Value
                          </span>
                          <span className="text-sm font-semibold text-text-900">
                            {kpi.value != null ? formatNumber.format(Number(kpi.value)) : '—'}
                          </span>
                        </div>
                        <div>
                          <span className="block text-[11px] font-medium uppercase tracking-wide text-text-400">
                            Change
                          </span>
                          <span className={cn('text-sm font-semibold', changeTone)}>{changeDisplay}</span>
                        </div>
                        <div>
                          <span className="block text-[11px] font-medium uppercase tracking-wide text-text-400">
                            Icon
                          </span>
                          <span className="text-sm font-semibold text-text-900">
                            {kpi.icon ?? 'gauge'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-2 text-xs text-text-500">
                      <span className="font-mono text-[11px] text-text-400">{kpi.id}</span>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-text-400 transition-colors hover:bg-transparent hover:text-text-900"
                          disabled={isPending}
                          onClick={(event) => {
                            event.stopPropagation();
                            void (status !== 'published' ? handlePublish(kpi.id) : handleRetire(kpi.id));
                          }}
                          title={status !== 'published' ? 'Publish' : 'Retire'}
                        >
                          {isPending && pendingAction?.id === kpi.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : status !== 'published' ? (
                            <Rocket className="h-4 w-4" />
                          ) : (
                            <ArchiveRestore className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-text-400 transition-colors hover:bg-transparent hover:text-brand-red"
                          disabled={isPending}
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleDelete(kpi.id);
                          }}
                          title="Delete"
                        >
                          {isPending && pendingAction?.action === 'delete' ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="canvas"
        padding="none"
        className="max-h-[86vh] w-[min(96vw,60rem)] overflow-hidden border border-border/40 bg-bg-0 shadow-[0_32px_96px_rgba(15,23,42,0.2)] sm:max-w-[60rem]"
      >
        <ModalBand tone="brand" spacing="md" />
        <div className="flex min-h-[min(560px,_86vh)] max-h-[86vh] flex-col bg-bg-0">
          <ModalHeader
            tone="neutral"
            spacing="md"
            className={cn(
              'sticky top-0 z-20 border-b border-border/60 bg-rose-surface px-8 py-6',
              headerElevated && 'shadow-[0_8px_18px_rgba(15,23,42,0.12)]'
            )}
          >
            <DialogTitle className="text-[18px] font-semibold text-text-900">
              Manage KPI Catalog
            </DialogTitle>
            <ModalSubtitle className="mt-2 text-[13px] leading-relaxed text-text-500">
              Curate the performance metrics that appear across overview dashboards. Draft metrics remain private until published.
            </ModalSubtitle>
          </ModalHeader>

          <div className="flex flex-1 min-h-0 flex-col lg:flex-row">
            <aside className="border-b border-border/60 bg-bg-0 lg:w-[340px] lg:border-b-0 lg:border-r">
              <div className="flex h-full flex-col gap-4 px-8 py-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-text-900">Existing KPIs</p>
                    <p className="text-xs text-text-500">
                      {kpis.length} metric{kpis.length === 1 ? '' : 's'} configured
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={resetToCreate}
                    data-testid="kpi-manager-new-button"
                    className="border-border/40 text-text-700 hover:bg-bg-1"
                  >
                    <Plus className="mr-1.5 h-4 w-4" />
                    New KPI
                  </Button>
                </div>
                <div ref={listScrollRef} className="flex-1 overflow-y-auto pr-1">
                  {renderList()}
                </div>
              </div>
            </aside>

            <section className="flex flex-1 min-h-0 flex-col bg-bg-0">
              <form onSubmit={handleSubmit} className="flex h-full flex-col" data-testid="kpi-manager-form">
                <div ref={contentScrollRef} className="flex-1 overflow-y-auto px-8 py-6">
                  <div className="flex flex-col gap-4 pb-6 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-text-900">
                        {mode === 'create' ? 'Create New KPI' : 'Edit KPI'}
                      </h3>
                      <p className="text-xs text-text-500">
                        {mode === 'create'
                          ? 'Define the metric, assign a display label, and set thresholds for alerting.'
                          : 'Modify the KPI values or supporting metadata. Saved changes are applied immediately.'}
                      </p>
                    </div>
                    {mode === 'edit' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={resetToCreate}
                        className="text-text-600 hover:text-brand-red"
                      >
                        <Ban className="mr-1.5 h-4 w-4" />
                        Cancel edit
                      </Button>
                    )}
                  </div>

                  {formError && (
                    <InlineAlert
                      tone="error"
                      description={formError}
                      className="mb-4"
                    />
                  )}

                  <div className="grid gap-4 lg:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="kpi-id" className="text-text-700">
                        Identifier
                      </Label>
                      <Input
                        id="kpi-id"
                        placeholder="unique_kpi_id"
                        value={form.id}
                        onChange={handleInputChange('id')}
                        disabled={mode === 'edit'}
                        className={inputBaseClass}
                      />
                      <p className="text-[11px] text-text-500">
                        Must be unique per tenant and cannot be changed after creation.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="kpi-label" className="text-text-700">
                        Label
                      </Label>
                      <Input
                        id="kpi-label"
                        placeholder="Total Portfolio Value"
                        value={form.label}
                        onChange={handleInputChange('label')}
                        className={inputBaseClass}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="kpi-value" className="text-text-700">
                        Value
                      </Label>
                      <Input
                        id="kpi-value"
                        placeholder="12500"
                        value={form.value}
                        onChange={handleInputChange('value')}
                        className={inputBaseClass}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="kpi-change" className="text-text-700">
                        Change (%)
                      </Label>
                      <Input
                        id="kpi-change"
                        placeholder="4.2"
                        value={form.change}
                        onChange={handleInputChange('change')}
                        className={inputBaseClass}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="kpi-comparison" className="text-text-700">
                        Comparison Window
                      </Label>
                      <Input
                        id="kpi-comparison"
                        placeholder="vs prior day"
                        value={form.comparison}
                        onChange={handleInputChange('comparison')}
                        className={inputBaseClass}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="kpi-icon" className="text-text-700">
                        Icon
                      </Label>
                      <Input
                        id="kpi-icon"
                        placeholder="gauge"
                        value={form.icon}
                        onChange={handleInputChange('icon')}
                        className={inputBaseClass}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="kpi-type" className="text-text-700">
                        Value Type
                      </Label>
                      <Select value={form.type} onValueChange={handleTypeChange}>
                        <SelectTrigger id="kpi-type" className={inputBaseClass}>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="number">Number</SelectItem>
                          <SelectItem value="percentage">Percentage</SelectItem>
                          <SelectItem value="currency">Currency</SelectItem>
                          <SelectItem value="time">Time</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2 lg:col-span-2">
                      <Label htmlFor="kpi-description" className="text-text-700">
                        Description (optional)
                      </Label>
                      <Textarea
                        id="kpi-description"
                        placeholder="Describe what this KPI represents and when it should be used."
                        value={form.description}
                        onChange={handleInputChange('description')}
                        className={inputBaseClass}
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2 lg:col-span-2">
                      <Label htmlFor="kpi-thresholds" className="text-text-700">
                        Thresholds JSON (optional)
                      </Label>
                      <Textarea
                        id="kpi-thresholds"
                        placeholder='{ "warning": 2000, "critical": 500 }'
                        value={form.thresholdsJson}
                        onChange={handleInputChange('thresholdsJson')}
                        className={cn(inputBaseClass, 'resize-y font-mono text-[13px] leading-relaxed')}
                        rows={4}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="kpi-sort-order" className="text-text-700">
                        Sort Order (optional)
                      </Label>
                      <Input
                        id="kpi-sort-order"
                        placeholder="0"
                        value={form.sortOrder}
                        onChange={handleInputChange('sortOrder')}
                        className={inputBaseClass}
                      />
                    </div>
                  </div>
                </div>

                <div
                  className={cn(
                    'border-t border-border/60 bg-bg-0 px-8 py-5',
                    footerElevated && 'shadow-[0_-8px_18px_rgba(15,23,42,0.08)]'
                  )}
                >
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <p className="text-xs text-text-500">
                      {mode === 'create'
                        ? 'New KPIs start in draft. Publish from the list when you are ready to surface the metric.'
                        : 'Saving applies changes immediately. Publish or retire the metric from the list on the left.'}
                    </p>
                    <Button
                      type="submit"
                      disabled={isSaving}
                      data-testid="kpi-manager-submit"
                      variant="default"
                      className="bg-brand-red px-6 text-white shadow-sm transition-colors hover:bg-brand-red-600 focus-visible:ring-brand-red/40"
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving…
                        </>
                      ) : mode === 'create' ? (
                        <>
                          <Plus className="mr-2 h-4 w-4" />
                          Create KPI
                        </>
                      ) : (
                        'Save changes'
                      )}
                    </Button>
                  </div>
                </div>
              </form>
            </section>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
